import { Plus, Key, Webhook, Building2, Users as UsersIcon, ShieldCheck, Bell, Database, Palette, CreditCard, Lock, Mail, Landmark } from "lucide-react";
import { Link } from "react-router-dom";
import RegistrationPaymentsPanel from "@/components/RegistrationPaymentsPanel";
import BrandPanel from "@/pages/admin/BrandPanel";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { usePersistedState } from "@/lib/persisted-state";
import { useTheme } from "@/components/ThemeProvider";
import { API_BASE } from "@/lib/utils";
import { fetchAiCredits } from "@/lib/aivory-one-api";
import { getAuthHeaders } from "@/lib/auth-fetch";
import { commonTimezones, DEFAULT_TIMEZONE } from "@/lib/timezone";
import ConnectedAccountsPanel from "@/components/ConnectedAccountsPanel";
import OwnershipTransferPanel from "@/components/OwnershipTransferPanel";
import { fetchGovWorkspaceSettings, saveGovWorkspaceSettings } from "@/lib/gov-integration-api";
import GovUpgradePrompt from "@/components/GovUpgradePrompt";
import { getAuthSession } from "@/lib/auth-session";

// const API_BASE = typeof window !== "undefined" ? "" : "http://localhost:8001";

const tabs = [
  { id: "team", label: "Team & roles", icon: UsersIcon },
  { id: "org", label: "Organization", icon: Building2 },
  { id: "brand", label: "White-label", icon: Palette },
  { id: "payments", label: "Registration pay", icon: CreditCard },
  { id: "security", label: "Security", icon: ShieldCheck },
  { id: "api", label: "API & webhooks", icon: Webhook },
  { id: "notif", label: "Notifications", icon: Bell },
  { id: "gov", label: "Government", icon: Landmark },
  { id: "data", label: "Data & retention", icon: Database },
];

export default function Settings() {
  const [team, setTeam] = useState([]);
  const { value, setValue, statusLabel } = usePersistedState("workspace.settings", {
    tab: "team",
    org: {
      workspaceName: "",
      legalEntity: "",
      currency: "USD",
      timezone: "Asia/Singapore",
      taxId: "",
      taxMode: "exclusive",
      taxRatePct: 0,
    },
    security: {
      samlDomain: "",
      samlEntityId: "",
      oidcIssuer: "",
      ssoStatus: "not_configured",
      ssoEnabled: false,
      enforce2fa: true,
      ipAllowListRanges: 3,
    },
    api: {
      liveKeyMasked: "",
      webhooks: [],
    },
    notifications: {
      alertEmails: "",
      slackWebhookUrl: "",
      types: {
        registrations: true,
        billing: true,
        ai: true,
        team: true,
        event_ops: true,
        integrations: true,
      },
      channels: {
        "New registration": { inApp: true, email: true, slack: false },
        "Refund requested": { inApp: true, email: true, slack: false },
        "Webhook failure": { inApp: true, email: true, slack: true },
        "Capacity 90%": { inApp: true, email: false, slack: true },
        "Device offline > 5min": { inApp: true, email: false, slack: false },
      },
    },
    data: {
      piiRetentionMonths: 24,
      marketingConsentDefault: "Region-aware",
    },
  });
  const tab = value.tab || "team";
  const setTab = (next) => setValue((prev) => ({ ...prev, tab: next }));
  const org = value.org || {};
  const setOrg = (next) => setValue((prev) => ({ ...prev, org: { ...prev.org, ...next } }));
  const security = value.security || {};
  const setSecurity = (next) => setValue((prev) => ({ ...prev, security: { ...prev.security, ...next } }));
  const api = value.api || {};
  const setApi = (next) => setValue((prev) => ({ ...prev, api: { ...prev.api, ...next } }));
  const notifications = value.notifications || {};
  const setNotifications = (next) => setValue((prev) => ({ ...prev, notifications: { ...prev.notifications, ...next } }));
  const data = value.data || {};
  const setData = (next) => setValue((prev) => ({ ...prev, data: { ...prev.data, ...next } }));

  // Apply the saved workspace theme to the live UI when the panel mounts or
  // the setting changes. Persist=true so the choice survives reloads --
  // ThemeProvider seeds from localStorage on next mount and avoids a flash
  // from the default before AdminLayout's fetch confirms the value.
  const { setTheme } = useTheme();
  useEffect(() => {
    const t = (org.theme || "").toLowerCase();
    if (t === "light" || t === "dark") setTheme(t, true);
  }, [org.theme, setTheme]);

  const [capa, setCapa] = useState(null);
  const [aiCredits, setAiCredits] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/capabilities`, { headers: getAuthHeaders() });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setCapa(data);
      } catch {
        if (!cancelled) setCapa(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!capa?.featureFlags?.ai_search) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchAiCredits();
        if (!cancelled) setAiCredits(data);
      } catch {
        if (!cancelled) setAiCredits(null);
      }
    })();
    return () => { cancelled = true; };
  }, [capa?.featureFlags?.ai_search]);

  const whitelabelEntitled = capa?.ok ? capa.featureFlags?.whitelabel === true : null;

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1500px] mx-auto" data-testid="settings-page">
      <div className="text-overline">Workspace · settings</div>
      <h1 className="font-heading text-4xl mt-2 tracking-tight font-medium">Settings</h1>
      {statusLabel ? <div className="text-xs text-[var(--text-secondary)] font-mono mt-2">{statusLabel}</div> : null}

      {capa?.ok ? <IntegrationOverview capa={capa} onOpenTab={setTab} /> : null}
      {aiCredits ? (
        <div className="mt-4 surface px-5 py-4 flex flex-wrap items-center justify-between gap-3 border border-[var(--border-default)]">
          <div>
            <div className="text-overline">AI credits</div>
            <p className="text-sm text-[var(--text-secondary)] mt-1">
              {aiCredits.used} of {aiCredits.monthlyAllowance} used this month · {aiCredits.remaining} remaining
            </p>
          </div>
          <Link to="/admin/billing" className="btn-secondary text-sm">View usage</Link>
        </div>
      ) : null}

      <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
        <div className="md:col-span-3">
          <div className="space-y-1">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`settings-tab-${t.id}`}
                className={`w-full text-left flex items-center gap-3 px-3 py-2.5 text-sm border-l-2 transition ${
                  tab === t.id ? "text-[var(--text-primary)] border-[var(--primary)] bg-[var(--bg-surface)]" : "text-[var(--text-secondary)] border-transparent hover:text-[var(--text-primary)]"
                }`}
              >
                <t.icon size={14} />
                <span className="flex-1">{t.label}</span>
                {t.id === "brand" && whitelabelEntitled === false ? (
                  <Lock size={12} className="text-[var(--text-tertiary)] shrink-0" aria-label="Enterprise plan required" />
                ) : null}
              </button>
            ))}
          </div>
        </div>

        <div className="md:col-span-9">
          {tab === "team" && <TeamPanel team={team} setTeam={setTeam} />}
          {tab === "org" && <OrgPanel org={org} setOrg={setOrg} />}
          {tab === "brand" && <BrandPanel />}
          {tab === "payments" && <RegistrationPaymentsPanel />}
          {tab === "security" && <SecurityPanel security={security} setSecurity={setSecurity} />}
          {tab === "api" && <ApiPanel api={api} setApi={setApi} />}
          {tab === "notif" && <NotifPanel notifications={notifications} setNotifications={setNotifications} />}
          {tab === "gov" && <GovWorkspacePanel />}
          {tab === "data" && <DataPanel data={data} setData={setData} />}
        </div>
      </div>
    </div>
  );
}

function formatSsoStatus(status) {
  const raw = String(status || "not_configured").replace(/_/g, " ").trim();
  if (!raw || raw === "not configured") return "Not configured";
  return raw.replace(/\b\w/g, (c) => c.toUpperCase());
}

function IntegrationOverview({ capa, onOpenTab }) {
  const items = [
    {
      id: "security",
      label: "Single sign-on",
      configured: Boolean(capa.sso?.configured),
      detail: capa.sso?.configured
        ? formatSsoStatus(capa.sso.status)
        : "Connect SAML or OIDC in Security",
    },
    {
      id: "api",
      label: "Webhooks",
      configured: Boolean(capa.webhooks?.configured),
      detail: capa.webhooks?.configured
        ? `${capa.webhooks.count} endpoint${capa.webhooks.count === 1 ? "" : "s"} configured`
        : "Add an endpoint in API & webhooks",
    },
    {
      id: "api",
      label: "API keys",
      configured: Boolean(capa.apiKeys?.configured),
      detail: capa.apiKeys?.configured
        ? `${capa.apiKeys.count} active key${capa.apiKeys.count === 1 ? "" : "s"}`
        : "Generate a key in API & webhooks",
    },
    {
      id: "notif",
      label: "Notifications",
      configured: Boolean(capa.notifications?.alertEmailsConfigured || capa.notifications?.slackConfigured),
      detail: (() => {
        const parts = [];
        if (capa.notifications?.alertEmailsConfigured) parts.push("email alerts");
        if (capa.notifications?.slackConfigured) parts.push("Slack");
        if (parts.length) return `${parts.join(" and ")} configured`;
        if (capa.notifications?.emailProviderConfigured === false) {
          return "Add alert emails or Slack webhook in Notifications";
        }
        return "Add alert emails or Slack webhook in Notifications";
      })(),
    },
    {
      id: "org",
      label: "Tax & billing",
      configured: Boolean(capa.tax?.rateSet && capa.tax?.taxIdPresent),
      detail: (() => {
        const missing = [];
        if (!capa.tax?.taxIdPresent) missing.push("tax ID");
        if (!capa.tax?.rateSet) missing.push("tax rate");
        if (!missing.length) return "Tax rate and ID on file";
        return `Add ${missing.join(" and ")} in Organization`;
      })(),
    },
  ];

  const pending = items.filter((item) => !item.configured).length;

  return (
    <div
      className={`mt-6 surface p-5 md:p-6 border ${
        pending ? "border-[color-mix(in_srgb,var(--primary)_35%,transparent)]" : "border-[var(--border-default)]"
      }`}
      data-testid="integration-overview"
    >
      <div>
        <div className="text-overline">Workspace</div>
        <h2 className="font-heading text-xl mt-1">Integration overview</h2>
        <p className="text-sm text-[var(--text-secondary)] mt-1">
          {pending
            ? `${pending} integration${pending === 1 ? " still needs" : "s still need"} setup.`
            : "Core integrations are configured."}
        </p>
      </div>
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {items.map((item) => (
          <button
            key={item.label}
            type="button"
            onClick={() => onOpenTab(item.id)}
            className="text-left surface p-4 border border-[var(--border-default)] hover:border-[var(--border-hover)] transition flex items-start justify-between gap-3 min-h-[5.5rem]"
            data-testid={`integration-item-${item.id}-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div className="min-w-0">
              <div className="font-medium text-sm">{item.label}</div>
              <div className="text-xs text-[var(--text-tertiary)] mt-1">{item.detail}</div>
            </div>
            <span
              className={`shrink-0 font-mono text-[10px] uppercase tracking-widest px-2 py-1 border ${
                item.configured
                  ? "text-brand border-[color-mix(in_srgb,var(--primary)_40%,transparent)]"
                  : "text-[var(--text-tertiary)] border-[var(--border-default)]"
              }`}
            >
              {item.configured ? "Ready" : "Setup needed"}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

const TEAM_ROLES = ["Admin", "Member", "Marketing", "Finance", "Onsite"];

function formatLastSeen(iso) {
  if (!iso) return "Never";
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "—";
  const diffMs = Date.now() - then.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 14) return `${days} day${days === 1 ? "" : "s"} ago`;
  return then.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function accessLabel(row) {
  if (!row.pending) return row.hasSignedIn ? "ENABLED" : row.passwordSetAt ? "READY" : "ENABLED";
  if (row.passwordSetAt || row.accessStatus === "accepted") return "READY";
  return "PENDING";
}

function statusLabelFor(row) {
  if (!row.pending) return formatLastSeen(row.lastLoginAt);
  if (row.passwordSetAt) return "Password set · not signed in yet";
  return "Invite sent";
}

function TeamPanel({ team, setTeam }) {
  const [serverInvites, setServerInvites] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteDraft, setInviteDraft] = useState({ name: "", email: "", role: "Admin" });
  const [inviteBusy, setInviteBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const reloadTeam = async () => {
    setLoading(true);
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 12000);
    try {
      const teamRes = await fetch(`${API_BASE}/api/admin/team`, { signal: controller.signal });
      if (!teamRes.ok) {
        throw new Error(teamRes.status === 502 ? "Server busy (502) — try again in a moment" : `Could not load team (${teamRes.status})`);
      }
      const data = await teamRes.json();
      const rawMembers = Array.isArray(data?.members) ? data.members : Array.isArray(data?.team) ? data.team : [];
      const seenMemberEmails = new Set();
      const members = rawMembers.filter((m) => {
        const em = String(m?.email || "").trim().toLowerCase();
        if (!em || seenMemberEmails.has(em)) return false;
        seenMemberEmails.add(em);
        return true;
      });
      setTeam(
        members.map((m, idx) => ({
          name: String(m?.name || `Member ${idx + 1}`),
          email: String(m?.email || ""),
          role: String(m?.role || "Member"),
          initials: String(m?.initials || "").trim() || String(m?.name || "M").trim().slice(0, 2).toUpperCase(),
          lastLoginAt: m?.lastLoginAt || null,
          passwordSetAt: m?.passwordSetAt || null,
          hasSignedIn: Boolean(m?.hasSignedIn),
          pending: false,
        })),
      );
      const memberEmails = new Set(
        members.map((m) => String(m?.email || "").trim().toLowerCase()).filter(Boolean),
      );
      const inviteRows = (Array.isArray(data?.invites) ? data.invites : [])
        .filter((p) => {
          const em = String(p?.email || "").trim().toLowerCase();
          return em && !memberEmails.has(em);
        })
        .map((p) => ({
          id: p.id,
          name: p.name || String(p?.email || "").split("@")[0] || "Invite",
          email: p.email,
          role: p.role || "Admin",
          initials: String(p.name || "I").slice(0, 2).toUpperCase(),
          lastLoginAt: p?.lastLoginAt || null,
          passwordSetAt: p?.passwordSetAt || null,
          hasSignedIn: Boolean(p?.hasSignedIn),
          accessStatus: p?.accessStatus || "pending",
          pending: true,
        }));
      setServerInvites(inviteRows);
    } catch (err) {
      toast.error("Team list unavailable", {
        description: err?.name === "AbortError" ? "Request timed out after 12s" : err?.message || "Unknown error",
      });
    } finally {
      window.clearTimeout(timeout);
      setLoading(false);
    }
  };

  useEffect(() => {
    reloadTeam();
  }, []);

  const sendInvite = async () => {
    const email = String(inviteDraft.email || "").trim().toLowerCase();
    if (!email) return;
    setInviteBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/team/invites`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          email,
          name: String(inviteDraft.name || "").trim(),
          role: String(inviteDraft.role || "Admin"),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `Invite failed (${res.status})`);
      toast.success(data?.emailSent ? "Invitation email sent" : "Invite saved", {
        description: data?.emailSent ? `${email} — check inbox for the 6-digit code` : `${email} — email delivery failed; use Resend`,
      });
      setInviteDraft({ name: "", email: "", role: "Admin" });
      setShowInvite(false);
      await reloadTeam();
    } catch (err) {
      toast.error("Invite failed", { description: err?.message });
    } finally {
      setInviteBusy(false);
    }
  };

  const updateMemberRole = async (memberEmail, role) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/team/members/${encodeURIComponent(memberEmail)}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ role }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Update failed");
      toast.success("Role updated");
      await reloadTeam();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const revokeMember = async (memberEmail) => {
    if (!window.confirm(`Revoke access for ${memberEmail}? They will no longer be able to sign in.`)) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/team/members/${encodeURIComponent(memberEmail)}`, { method: "DELETE" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Revoke failed");
      toast.success("Access revoked");
      await reloadTeam();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const updateInviteRole = async (inviteId, role) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/team/invites/${encodeURIComponent(inviteId)}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ role }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Update failed");
      toast.success("Invite updated");
      await reloadTeam();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const revokeInvite = async (inviteId, email) => {
    if (!window.confirm(`Revoke invitation for ${email}?`)) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/team/invites/${encodeURIComponent(inviteId)}`, { method: "DELETE" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Revoke failed");
      toast.success("Invitation revoked");
      await reloadTeam();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const resendInvite = async (inviteId) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/team/invites/${encodeURIComponent(inviteId)}/resend`, { method: "POST" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Resend failed");
      toast.success(data?.emailSent ? "Invitation resent" : "Resend failed", {
        description: data?.emailSent ? "New setup link sent" : "Check Brevo configuration",
      });
    } catch (err) {
      toast.error(err.message);
    }
  };

  const memberRows = (Array.isArray(team) ? team : []).map((m) => ({ ...m, pending: false, id: null }));
  const inviteRows = Array.isArray(serverInvites) ? serverInvites : [];
  const rows = [...memberRows, ...inviteRows];

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl">Team & roles</h2>
        <button className="btn-primary" onClick={() => setShowInvite((v) => !v)}><Plus size={14} /> Invite member</button>
      </div>
      {showInvite ? (
        <div className="surface p-4 mt-4 grid md:grid-cols-4 gap-2">
          <input className="input-dark text-sm" placeholder="Name" value={inviteDraft.name} onChange={(e) => setInviteDraft((p) => ({ ...p, name: e.target.value }))} />
          <input className="input-dark text-sm" placeholder="Email" type="email" value={inviteDraft.email} onChange={(e) => setInviteDraft((p) => ({ ...p, email: e.target.value }))} />
          <select className="input-dark text-sm" value={inviteDraft.role} onChange={(e) => setInviteDraft((p) => ({ ...p, role: e.target.value }))}>
            {TEAM_ROLES.map((r) => (
              <option key={r}>{r}</option>
            ))}
          </select>
          <button className="btn-primary justify-center text-sm" onClick={sendInvite} disabled={inviteBusy}>
            {inviteBusy ? "Sending…" : "Send invite"}
          </button>
        </div>
      ) : null}
      <p className="mt-3 text-xs text-[var(--text-tertiary)] leading-relaxed max-w-2xl">
        Invitations email a secure link to set a password. Pending members can be amended or revoked before they accept.
      </p>
      <div className="mt-6 surface overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="border-b border-[var(--border-default)] text-[var(--text-tertiary)] font-mono text-[10px] uppercase tracking-widest">
            <tr>
              <th className="p-4 text-left">Member</th>
              <th className="p-4 text-left">Role</th>
              <th className="p-4 text-left">Access</th>
              <th className="p-4 text-left">Last sign-in</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="p-8 text-[var(--text-tertiary)]">Loading team…</td>
              </tr>
            ) : (
              rows.map((m) => (
                <tr key={`${m.email}-${m.pending ? m.id : "active"}`} className="border-b border-[var(--border-default)]">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <Avatar className="w-8 h-8 rounded-none border border-[var(--border-default)]">
                        <AvatarFallback className="bg-[var(--bg-elev)] rounded-none text-xs">{m.initials}</AvatarFallback>
                      </Avatar>
                      <div>
                        <div>{m.name}</div>
                        <div className="text-xs font-mono text-[var(--text-tertiary)]">{m.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    {m.pending ? (
                      <select
                        className="input-dark text-xs font-mono uppercase"
                        value={m.role}
                        onChange={(e) => updateInviteRole(m.id, e.target.value)}
                      >
                        {TEAM_ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <select
                        className="input-dark text-xs font-mono uppercase"
                        value={m.role}
                        disabled={String(m.role).toLowerCase() === "owner"}
                        onChange={(e) => updateMemberRole(m.email, e.target.value)}
                      >
                        {String(m.role).toLowerCase() === "owner" ? (
                          <option value="Owner">Owner</option>
                        ) : null}
                        {TEAM_ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td className="p-4 text-xs font-mono text-[var(--primary)]">{accessLabel(m)}</td>
                  <td className="p-4 text-xs font-mono text-[var(--text-secondary)]">{statusLabelFor(m)}</td>
                  <td className="p-4 text-right">
                    <div className="flex justify-end gap-2 flex-wrap">
                      {m.pending ? (
                        <>
                          <button type="button" className="btn-ghost text-xs" onClick={() => resendInvite(m.id)}>
                            Resend
                          </button>
                          <button type="button" className="btn-ghost text-xs text-red-400" onClick={() => revokeInvite(m.id, m.email)}>
                            Revoke
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="btn-ghost text-xs text-red-400"
                          disabled={String(m.role).toLowerCase() === "owner"}
                          onClick={() => revokeMember(m.email)}
                        >
                          Revoke
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
            {!loading && rows.length === 0 ? (
              <tr>
                <td className="p-4 text-sm text-[var(--text-secondary)]" colSpan={5}>No team members available.</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <h3 className="font-heading text-xl mt-12 mb-4">Permission matrix</h3>
      <div className="surface overflow-x-auto">
        <table className="w-full text-sm min-w-[600px]">
          <thead className="border-b border-[var(--border-default)] text-[var(--text-tertiary)] font-mono text-[10px] uppercase tracking-widest">
            <tr>
              <th className="p-4 text-left">Capability</th><th className="p-4 text-center">Owner</th><th className="p-4 text-center">Admin</th><th className="p-4 text-center">Marketing</th><th className="p-4 text-center">Onsite</th><th className="p-4 text-center">Finance</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["Create / edit events", true, true, false, false, false],
              ["Edit ticket prices", true, true, false, false, false],
              ["Issue refunds", true, true, false, false, true],
              ["Send campaign emails", true, true, true, false, false],
              ["Run check-in", true, true, false, true, false],
              ["Export financial data", true, true, false, false, true],
            ].map((row, i) => (
              <tr key={i} className="border-b border-[var(--border-default)]">
                <td className="p-4 text-[var(--text-secondary)]">{row[0]}</td>
                {row.slice(1).map((v, j) => <td key={j} className="p-4 text-center font-mono text-[var(--primary)]">{v ? "●" : <span className="text-[var(--border-default)]">○</span>}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OrgPanel({ org, setOrg }) {
  return (
    <div className="space-y-6">
      <h2 className="font-heading text-2xl">Organization</h2>
      <div className="surface p-6 space-y-4 max-w-xl">
        <div><label className="text-overline">Workspace name</label><input className="input-dark mt-2" value={org.workspaceName || ""} onChange={(e) => setOrg({ workspaceName: e.target.value })} /></div>
        <div><label className="text-overline">Legal entity</label><input className="input-dark mt-2" value={org.legalEntity || ""} onChange={(e) => setOrg({ legalEntity: e.target.value })} /></div>
        <div><label className="text-overline">Default currency</label><select className="input-dark mt-2" value={org.currency || "USD"} onChange={(e) => setOrg({ currency: e.target.value })}><option>USD</option><option>EUR</option><option>GBP</option><option>SGD</option></select></div>
        <div>
          <label className="text-overline">Display timezone</label>
          <select
            className="input-dark mt-2"
            value={org.timezone || DEFAULT_TIMEZONE}
            onChange={(e) => setOrg({ timezone: e.target.value })}
            data-testid="settings-display-timezone"
          >
            {commonTimezones.map((z) => (
              <option key={z.value} value={z.value}>
                {z.label}
              </option>
            ))}
          </select>
          <div className="text-[10px] font-mono text-[#71717A] mt-1">
            Used for report timestamps and check-in dashboards. Default: Asia/Singapore (GMT+8).
          </div>
        </div>
        <div><label className="text-overline">Tax ID</label><input className="input-dark mt-2 font-mono" value={org.taxId || ""} onChange={(e) => setOrg({ taxId: e.target.value })} /></div>
        <div><label className="text-overline">Tax mode</label><select className="input-dark mt-2" value={org.taxMode || "exclusive"} onChange={(e) => setOrg({ taxMode: e.target.value })}><option value="exclusive">Exclusive</option><option value="inclusive">Inclusive</option><option value="exempt">Exempt</option></select></div>
        <div><label className="text-overline">Default tax rate (%)</label><input className="input-dark mt-2 font-mono" type="number" min="0" max="100" step="0.01" value={Number(org.taxRatePct || 0)} onChange={(e) => setOrg({ taxRatePct: Number(e.target.value) })} /></div>
        <div>
          <label className="text-overline">Admin UI theme</label>
          <select
            className="input-dark mt-2"
            value={org.theme || "dark"}
            onChange={(e) => setOrg({ theme: e.target.value })}
            data-testid="settings-theme"
          >
            <option value="dark">Dark</option>
            <option value="light">Light</option>
          </select>
          <div className="text-[10px] font-mono text-[#71717A] mt-1">
            Applies to every admin page. Public event landings use each event's own theme (set under Event basics).
          </div>
        </div>
      </div>

      <OwnershipTransferPanel />
    </div>
  );
}

function SecurityPanel({ security, setSecurity }) {
  const hasSamlDomain = String(security.samlDomain || "").trim().length > 0;
  const hasOidc = String(security.oidcIssuer || "").trim().length > 0;
  return (
    <div className="space-y-6">
      <h2 className="font-heading text-2xl">Security</h2>
      <ConnectedAccountsPanel />
      <div className="surface p-5 space-y-4">
        <div className="font-medium">SSO connection status</div>
        <p className="text-xs text-[var(--text-tertiary)]">Login shows SSO only when status is <span className="font-mono text-[var(--text-secondary)]">active</span> and OIDC issuer is set.</p>
        <select
          className="input-dark max-w-md"
          value={security.ssoStatus || "not_configured"}
          onChange={(e) => setSecurity({ ssoStatus: e.target.value })}
        >
          <option value="not_configured">Not configured</option>
          <option value="testing">Testing</option>
          <option value="active">Active</option>
        </select>
      </div>
      <div className="surface p-5 space-y-3">
        <div className="font-medium">OIDC issuer URL</div>
        <input
          className="input-dark text-xs font-mono w-full"
          placeholder="https://your-idp.example.com/realms/workspace"
          value={security.oidcIssuer || ""}
          onChange={(e) => setSecurity({ oidcIssuer: e.target.value })}
        />
      </div>
      <div className="surface p-5 space-y-3">
        <div className="font-medium">SAML — IdP domain / entity</div>
        <input
          className="input-dark text-xs font-mono w-full"
          placeholder="idp.customer.com"
          value={security.samlDomain || ""}
          onChange={(e) => setSecurity({ samlDomain: e.target.value })}
        />
        <input
          className="input-dark text-xs font-mono w-full"
          placeholder="Entity ID (optional)"
          value={security.samlEntityId || ""}
          onChange={(e) => setSecurity({ samlEntityId: e.target.value })}
        />
        <div className="text-xs text-[var(--text-tertiary)] font-mono">{hasSamlDomain || hasOidc ? "Identity provider fields present" : "Not configured"}</div>
      </div>
      <div className="surface p-5 flex items-center justify-between opacity-80">
        <div>
          <div className="font-medium text-[var(--text-tertiary)]">Legacy “SSO enabled” toggle</div>
          <div className="text-xs text-[#52525B]">Superseded by status dropdown; kept for backwards compatibility.</div>
        </div>
        <button
          type="button"
          className={`font-mono text-[10px] uppercase tracking-widest border px-2 py-1 ${security.ssoEnabled ? "text-[var(--primary)] border-[var(--primary)]/40" : "text-[var(--text-secondary)] border-[var(--border-default)]"}`}
          onClick={() => setSecurity({ ssoEnabled: !security.ssoEnabled })}
          disabled={!hasSamlDomain && !hasOidc}
        >
          {security.ssoEnabled ? "On" : "Off"}
        </button>
      </div>
      <div className="surface p-5 flex items-center justify-between">
        <div><div className="font-medium">Enforce 2FA for all members</div><div className="text-xs text-[var(--text-tertiary)] font-mono mt-1">Required at next login</div></div>
        <button
          type="button"
          className={`font-mono text-[10px] uppercase tracking-widest border px-2 py-1 ${security.enforce2fa ? "text-[var(--primary)] border-[var(--primary)]/40" : "text-[var(--text-secondary)] border-[var(--border-default)]"}`}
          onClick={() => setSecurity({ enforce2fa: !security.enforce2fa })}
        >
          {security.enforce2fa ? "On" : "Off"}
        </button>
      </div>
      <div className="surface p-5 flex items-center justify-between">
        <div><div className="font-medium">IP allow-list</div><div className="text-xs text-[var(--text-tertiary)] font-mono mt-1">{security.ipAllowListRanges || 0} ranges</div></div>
        <div className="flex items-center gap-2">
          <button className="btn-ghost text-sm" type="button" onClick={() => setSecurity({ ipAllowListRanges: Math.max(0, Number(security.ipAllowListRanges || 0) - 1) })}>-</button>
          <button className="btn-ghost text-sm" type="button" onClick={() => setSecurity({ ipAllowListRanges: Number(security.ipAllowListRanges || 0) + 1 })}>+</button>
        </div>
      </div>
    </div>
  );
}

function ApiPanel({ api, setApi }) {
  const webhooks = Array.isArray(api.webhooks) ? api.webhooks : [];
  const hasKey = String(api.liveKeyMasked || "").trim().length > 0;
  const hasWebhook = webhooks.some((w) => String(w?.url || "").trim().length > 0);
  const [deliveryRows, setDeliveryRows] = useState([]);
  const [testingIdx, setTestingIdx] = useState(-1);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/webhooks/deliveries`);
        if (!res.ok) return;
        const payload = await res.json();
        if (!cancelled) setDeliveryRows(Array.isArray(payload?.items) ? payload.items : []);
      } catch {
        if (!cancelled) setDeliveryRows([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/integrations`);
        if (!res.ok) return;
        const payload = await res.json();
        const integ = payload?.integration || {};
        if (cancelled || !Array.isArray(integ.webhooks) || integ.webhooks.length === 0) return;
        setApi((prev) => {
          const existing = Array.isArray(prev?.webhooks) ? prev.webhooks : [];
          const hasUrl = existing.some((w) => String(w?.url || "").trim());
          if (hasUrl) return prev;
          return { ...prev, webhooks: integ.webhooks };
        });
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setApi]);
  const updateWebhook = (idx, patch) => {
    const next = webhooks.map((row, i) => (i === idx ? { ...row, ...patch } : row));
    setApi({ webhooks: next });
  };
  const triggerWebhookTest = async (idx) => {
    const row = webhooks[idx] || {};
    if (!String(row?.url || "").trim()) return;
    try {
      setTestingIdx(idx);
      const res = await fetch(`${API_BASE}/api/admin/webhooks/test`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ url: row.url, events: row.events }),
      });
      if (!res.ok) throw new Error("Webhook test failed");
      const data = await res.json();
      setDeliveryRows((prev) => [data.item, ...prev].slice(0, 20));
    } catch {
      // keep UI stable
    } finally {
      setTestingIdx(-1);
    }
  };
  return (
    <div className="space-y-6">
      <h2 className="font-heading text-2xl">API & webhooks</h2>
      <div className="surface p-5">
        <div className="flex items-center gap-2 text-overline mb-3"><Key size={11} /> Live key</div>
        <input
          className="input-dark font-mono text-sm"
          value={api.liveKeyMasked || ""}
          onChange={(e) => setApi({ liveKeyMasked: e.target.value })}
        />
        <div className="text-xs text-[var(--text-tertiary)] font-mono mt-2">{hasKey ? "API key configured" : "Not configured: create a live API key."}</div>
      </div>
      <div className="surface p-5">
        <div className="flex items-center gap-2 text-overline mb-3"><Webhook size={11} /> Webhooks</div>
        <div className="space-y-3">
          {webhooks.map((row, idx) => (
            <div key={`${row.url}-${idx}`} className="grid grid-cols-12 gap-2 border border-[var(--border-default)] px-3 py-2 text-xs font-mono">
              <input
                className="input-dark col-span-7"
                value={row.url || ""}
                onChange={(e) => updateWebhook(idx, { url: e.target.value })}
              />
              <input
                className="input-dark col-span-3 text-[var(--text-secondary)]"
                value={row.events || ""}
                onChange={(e) => updateWebhook(idx, { events: e.target.value })}
              />
              <button className="btn-secondary col-span-2 text-[10px] justify-center" onClick={() => triggerWebhookTest(idx)} disabled={testingIdx === idx || !String(row.url || "").trim()}>
                {testingIdx === idx ? "TESTING..." : "TEST"}
              </button>
            </div>
          ))}
        </div>
        <div className="text-xs text-[var(--text-tertiary)] font-mono mt-3">{hasWebhook ? "Webhook endpoint configured" : "Not configured: add at least one webhook endpoint."}</div>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-secondary text-xs"
            onClick={async () => {
              try {
                const res = await fetch(`${API_BASE}/api/admin/integrations`, {
                  method: "PUT",
                  headers: { "content-type": "application/json" },
                  body: JSON.stringify({
                    value: {
                      webhooks,
                      apiKeys: [{ id: "key_ui", name: "Console key", masked: api.liveKeyMasked || "" }],
                    },
                  }),
                });
                if (!res.ok) throw new Error("Save failed");
                toast.success("Webhooks saved", { description: "Endpoints stored for this workspace." });
              } catch (err) {
                toast.error(err?.message || "Could not save integration store");
              }
            }}
          >
            Persist webhooks to server store
          </button>
        </div>
        <div className="mt-4 border border-[var(--border-default)]">
          <div className="px-3 py-2 text-overline border-b border-[var(--border-default)]">Recent deliveries</div>
          <div className="max-h-40 overflow-y-auto divide-y divide-[var(--border-default)]">
            {deliveryRows.map((d) => (
              <div key={d.id} className="px-3 py-2 text-[11px] font-mono flex items-center justify-between gap-2">
                <span className="truncate text-[var(--text-secondary)]">{d.events} · {d.url}</span>
                <span className={String(d.status || "").toLowerCase() === "failed" ? "text-[var(--error)]" : "text-[var(--primary)]"}>{d.responseCode || "—"}</span>
              </div>
            ))}
            {!deliveryRows.length ? <div className="px-3 py-2 text-[11px] font-mono text-[var(--text-tertiary)]">No deliveries yet.</div> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function NotifPanel({ notifications, setNotifications }) {
  const items = [
    "New registration", "Refund requested", "Webhook failure", "Capacity 90%", "Device offline > 5min",
  ];
  const typeDefs = [
    { id: "registrations", label: "Registrations", description: "New registrations, capacity alerts, and refund requests" },
    { id: "billing", label: "Billing & trials", description: "Invoices, payment failures, and trial reminders" },
    { id: "ai", label: "AI usage", description: "Credit threshold and exhaustion alerts" },
    { id: "team", label: "Team & security", description: "Invites, member joins, API keys, and account security" },
    { id: "event_ops", label: "Event operations", description: "Event publish, form publish, and website go-live" },
    { id: "integrations", label: "Integrations", description: "Payment connections and webhook failures" },
  ];
  const channels = notifications.channels || {};
  const types = {
    registrations: true,
    billing: true,
    ai: true,
    team: true,
    event_ops: true,
    integrations: true,
    ...(notifications.types || {}),
  };
  const alertEmails = notifications.alertEmails || "";
  const slackWebhookUrl = notifications.slackWebhookUrl || "";
  const [testing, setTesting] = useState("");

  const patch = (next) => setNotifications(next);

  const setType = (id, value) => {
    patch({
      types: {
        ...types,
        [id]: Boolean(value),
      },
    });
  };

  const setChannel = (item, key, value) => {
    patch({
      channels: {
        ...channels,
        [item]: {
          ...(channels[item] || {}),
          [key]: value,
        },
      },
    });
  };

  const runTest = async (channel) => {
    setTesting(channel);
    try {
      const res = await fetch(`${API_BASE}/api/admin/notifications/test`, {
        method: "POST",
        headers: { "content-type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ channel }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `Test failed (${res.status})`);
      if (channel === "inApp") {
        toast.success("In-app test sent", { description: "Check the bell icon in the top bar." });
      } else if (channel === "email") {
        toast.success("Email test sent", { description: data.detail || `Sent to ${(data.sentTo || []).join(", ")}` });
      } else {
        toast.success("Slack test sent", { description: data.detail || "Posted to your Slack channel." });
      }
    } catch (err) {
      toast.error("Test failed", { description: err?.message || "Could not deliver test notification" });
    } finally {
      setTesting("");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl">Notifications</h2>
        <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-2xl">
          Choose which notification types you want, then pick delivery channels. In-app alerts appear in the top bar bell.
          Email uses your server mail provider. Slack uses an incoming webhook.
        </p>
      </div>

      <div className="surface p-5 border border-[var(--border-default)] bg-[var(--surface-elevated)]/40">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-overline flex items-center gap-2"><Mail className="w-4 h-4" /> System email templates</div>
            <p className="text-sm text-[var(--text-secondary)] mt-1 max-w-xl">
              Customize transactional emails (welcome, billing, team invites, AI alerts, and more). Defaults can be overridden per workspace.
            </p>
          </div>
          <Link to="/admin/settings/system-emails" className="btn-primary text-sm shrink-0">
            Manage templates
          </Link>
        </div>
      </div>

      <div className="surface divide-y divide-[var(--border-default)]" data-testid="notif-types">
        <div className="px-4 py-3">
          <div className="text-overline">Notification types</div>
          <p className="text-xs text-[var(--text-tertiary)] mt-1">
            Turn off a type to stop related system emails and workspace alerts for this workspace.
          </p>
        </div>
        {typeDefs.map((row) => (
          <label key={row.id} className="p-4 flex items-start justify-between gap-4 cursor-pointer hover:bg-[var(--bg-elev)]/40">
            <div className="min-w-0">
              <div className="font-medium text-sm">{row.label}</div>
              <p className="text-xs text-[var(--text-secondary)] mt-1">{row.description}</p>
            </div>
            <input
              type="checkbox"
              className="accent-[var(--primary)] mt-1 shrink-0"
              checked={Boolean(types[row.id])}
              onChange={(e) => setType(row.id, e.target.checked)}
              data-testid={`notif-type-${row.id}`}
            />
          </label>
        ))}
      </div>

      <div className="surface p-5 space-y-4">
        <div className="text-overline">Delivery setup</div>
        <label className="block">
          <span className="text-overline">Alert email addresses</span>
          <input
            className="input-dark mt-2 font-mono text-sm"
            placeholder="ops@yourcompany.com, finance@yourcompany.com"
            value={alertEmails}
            onChange={(e) => patch({ alertEmails: e.target.value })}
            data-testid="notif-alert-emails"
          />
          <span className="text-[10px] font-mono text-[var(--text-tertiary)] mt-1 block">
            Comma-separated recipients for email alerts when email is enabled below.
          </span>
        </label>
        <label className="block">
          <span className="text-overline">Slack incoming webhook</span>
          <input
            className="input-dark mt-2 font-mono text-sm"
            placeholder="https://hooks.slack.com/services/..."
            value={slackWebhookUrl}
            onChange={(e) => patch({ slackWebhookUrl: e.target.value })}
            data-testid="notif-slack-webhook"
          />
          <span className="text-[10px] font-mono text-[var(--text-tertiary)] mt-1 block">
            Create one in Slack → Apps → Incoming Webhooks, then paste the URL here.
          </span>
        </label>
        <div className="flex flex-wrap gap-2 pt-1">
          <button type="button" className="btn-secondary text-xs" disabled={Boolean(testing)} onClick={() => runTest("inApp")}>
            {testing === "inApp" ? "Sending…" : "Test in-app"}
          </button>
          <button type="button" className="btn-secondary text-xs" disabled={Boolean(testing)} onClick={() => runTest("email")}>
            {testing === "email" ? "Sending…" : "Test email"}
          </button>
          <button type="button" className="btn-secondary text-xs" disabled={Boolean(testing)} onClick={() => runTest("slack")}>
            {testing === "slack" ? "Sending…" : "Test Slack"}
          </button>
        </div>
      </div>

      <div className="surface divide-y divide-[var(--border-default)]">
        <div className="px-4 py-3 text-overline">Event alerts · channels</div>
        {items.map((n) => (
          <div key={n} className="p-4 flex flex-wrap items-center justify-between gap-3">
            <span className="font-medium text-sm">{n}</span>
            <div className="flex flex-wrap gap-4 text-xs font-mono text-[var(--text-tertiary)]">
              <label className="flex items-center gap-2">
                <input type="checkbox" className="accent-[var(--primary)]" checked={Boolean(channels[n]?.inApp)} onChange={(e) => setChannel(n, "inApp", e.target.checked)} />
                IN-APP
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" className="accent-[var(--primary)]" checked={Boolean(channels[n]?.email)} onChange={(e) => setChannel(n, "email", e.target.checked)} />
                EMAIL
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" className="accent-[var(--primary)]" checked={Boolean(channels[n]?.slack)} onChange={(e) => setChannel(n, "slack", e.target.checked)} />
                SLACK
              </label>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DataPanel({ data, setData }) {
  return (
    <div className="space-y-6">
      <h2 className="font-heading text-2xl">Data & retention</h2>
      <div className="surface p-5">
        <div className="text-overline mb-2">PII retention</div>
        <input
          type="range"
          min="6"
          max="120"
          value={Number(data.piiRetentionMonths || 24)}
          onChange={(e) => setData({ piiRetentionMonths: Number(e.target.value) })}
          className="w-full accent-[var(--primary)]"
        />
        <div className="font-mono text-xs text-[var(--text-tertiary)] mt-2">{Number(data.piiRetentionMonths || 24)} months after event end</div>
      </div>
      <div className="surface p-5">
        <div className="text-overline mb-3">Marketing consent default</div>
        <div className="flex gap-3">
          {["Opt-in", "Opt-out", "Region-aware"].map((o) => (
            <button
              key={o}
              type="button"
              onClick={() => setData({ marketingConsentDefault: o })}
              className={`text-xs font-mono px-3 py-2 border ${data.marketingConsentDefault === o ? "border-[var(--primary)] text-[var(--primary)]" : "border-[var(--border-default)] text-[var(--text-secondary)]"}`}
            >
              {o}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function GovWorkspacePanel() {
  const [gov, setGov] = useState({ enabled: true, defaultRegistrationBackend: "native", plumberIpAllowlist: false });
  const [entitled, setEntitled] = useState(false);
  const [plan, setPlan] = useState("studio");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const authEmail = getAuthSession()?.email || "";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchGovWorkspaceSettings();
        if (!cancelled) {
          setGov((g) => ({ ...g, ...(data.gov || {}) }));
          setEntitled(data.entitled === true);
          setPlan(data.plan || "studio");
        }
      } catch {
        if (!cancelled) {
          setGov({ enabled: true, defaultRegistrationBackend: "native", plumberIpAllowlist: false });
          setEntitled(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const saved = await saveGovWorkspaceSettings(gov);
      setGov(saved);
      toast.success("Government workspace settings saved");
    } catch (err) {
      toast.error(err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-sm text-[var(--text-tertiary)]">Loading…</p>;

  if (!entitled) {
    return <GovUpgradePrompt plan={plan} defaultEmail={authEmail} />;
  }

  return (
    <div className="space-y-6 max-w-2xl" data-testid="gov-workspace-settings">
      <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
        FormSG + Plumber is an <strong>Enterprise</strong> capability. Configure each event under Event detail → Government integration for the Plumber URL, secret, and FormSG → Aivory field mapping (FSGID → submissionId, AttendanceType → ticketTier; Name/Email stay in Excel).
      </p>
      <div>
        <label className="text-overline block mb-2">Default registration backend for new events</label>
        <select className="input-dark w-full max-w-md" value={gov.defaultRegistrationBackend || "native"} onChange={(e) => setGov((g) => ({ ...g, defaultRegistrationBackend: e.target.value }))}>
          <option value="native">AivoryEMS native</option>
          <option value="formsg">FormSG</option>
        </select>
      </div>
      <label className="flex items-center gap-3 text-sm text-[var(--text-tertiary)]">
        <input type="checkbox" className="accent-[var(--primary)]" checked={gov.plumberIpAllowlist === true} onChange={(e) => setGov((g) => ({ ...g, plumberIpAllowlist: e.target.checked }))} />
        Optional: restrict ingest to Plumber government IPs only (off by default — use secret header instead)
      </label>
      <button type="button" className="btn-primary" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save"}</button>
    </div>
  );
}
